import React, { useEffect, useState } from 'react';
import { Button, Typography, Table, TableHead, TableRow, TableCell, TableBody, Box, TablePagination } from '@mui/material';
import { FaSearch } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance';
import Profile from '../../components/Profile';
import Notification from '../../components/Notification';
import Input from '../../components/ui/Input';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';


const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [collegeId, setCollegeId] = useState(null);
  const [username, setUsername] = useState(null);
  const [displayname, setDisplayname] = useState(null);

  const [page, setPage] = useState(0); // page number
  const [rowsPerPage, setRowsPerPage] = useState(10); // rows per page

  const handleExportCSV = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).replace(/ /g, ''); // e.g., 27Apr2025
  
    const filename = `users_${formattedDate}.csv`;
  
    // 🛑 Add more headers if needed
    const headers = ["Username", "Email", "Mobile", "Display Name", "Role", "College ID", "Course IDs", "Created At"];
  
    const rows = filteredUsers.map(user => [
      user.username,
      user.email,
      user.mobile,
      user.displayname,
      user.role,
      user.collegeId || '',
      user.courseIds ? user.courseIds.join('; ') : '', // separate courseIds by ;
      user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''
    ]);
  
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(","))
    ].join("\n");
  
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
    toast.success("User list exported successfully!");
  };
  

  const fetchUsers = async () => {
    try {
      const res = await axios.get('https://backend.scholarkart.in/api/college-users');
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setUsername(user.username);
      setCollegeId(user.collegeId);
      setDisplayname(user.displayname);
    }
  }, []);

  const handleEdit = (userId) => {
    navigate(`/edituser/${userId}`);
  };

  const handleAddUser = () => {
    navigate('/adduser');
  };

  const handleDeleteUser = () => {
    navigate('/deleteuser');
  };

  // 🛑 Filter users based on search input
  const filteredUsers = users.filter((user) => {
    const query = inputValue.toLowerCase();
    return (
      user.username?.toLowerCase().includes(query) ||
      user.displayname?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.mobile?.toLowerCase().includes(query)
    );
  });

  // 🛑 Pagination calculations
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  return (
    <main className="flex-grow p-1">
      <div className="flex justify-between items-center relative bg-white p-4 rounded-lg shadow-md">
        {collegeId && (
          <div className="relative w-full max-w-lg">
            <Input
              placeholder="Search my library users..."
              className="pl-10"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-3 text-gray-500" />
          </div>
        )}
        <div className="flex items-center space-x-4 relative">
          <Notification />
          <Profile />
        </div>
      </div>

      <Box p={4}>
        <Typography variant="h4" fontWeight="bold" mb={4}>User Management</Typography>

        <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
          <Button variant="contained" color="primary" onClick={handleAddUser}>Add User</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser}>Delete User</Button>
          <Button variant="contained" color="success" onClick={handleExportCSV}>Export Users</Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.mobile}</TableCell>
                <TableCell>{user.displayname}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  <Button variant="outlined" color="secondary" onClick={() => handleEdit(user._id)}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* 🛑 Pagination controls */}
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Box>
    </main>
  );
};

export default UserManagement;
